import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { motion, AnimateSharedLayout, AnimatePresence, MotionConfig } from "framer-motion";
import useResizeObserver from "@react-hook/resize-observer";
import { t } from "../theme";
import { UI_BODY_SEMIBOLD_SM_DF, icons } from "oolib";

type IconName = keyof typeof icons;
// A tab icon can be an oolib icon name (looked up in `icons`) or a ready-made
// icon component (e.g. a Phosphor `*Icon`), rendered with size/weight/color.
type IconComponent = React.ComponentType<{ size?: number | string; weight?: string; color?: string }>;

const PILL_TRANSITION = { type: "spring", stiffness: 500, damping: 40 } as const;
const PANEL_TRANSITION = { type: "tween", duration: 0.27, ease: [0.32, 0.72, 0, 1] } as const;

export type AnimatedTab<T extends string> = {
  value: T;
  label: string;
  icon?: IconName | IconComponent;
};

type Padding = string | number;

type AnimatedTabsProps<T extends string> = {
  tabs: AnimatedTab<T>[];
  value: T;
  onChange: (value: T) => void;
  padding?: Padding;
  /** Optional content pinned to the right edge of the bar (e.g. a Clear Filter
   *  button). When present the bar spreads full width with space-between. */
  actions?: React.ReactNode;
};

const Bar = styled.div<{ $padding?: Padding; $spread?: boolean }>`
  display: ${({ $spread }) => ($spread ? "flex" : "inline-flex")};
  align-items: center;
  ${({ $spread }) => ($spread ? "width: 100%; justify-content: space-between;" : "")}
  ${({ $padding }) =>
    $padding !== undefined
      ? `padding: ${typeof $padding === "number" ? `${$padding}px` : $padding};`
      : ""}
`;

// Groups the tab buttons so the actions slot can sit opposite them under
// the bar's space-between. min-width:0 lets the group hold a definite (bounded)
// width. When $fade is on (a long filter label with a Clear action), the group
// clips its overflow and fades the right edge instead of wrapping or pushing the
// actions off the bar. Short labels leave trailing space, so the fade is unseen.
const TabButtonGroup = styled.div<{ $fade?: boolean }>`
  display: inline-flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  ${({ $fade }) =>
    $fade
      ? `
    overflow: hidden;
    mask-image: linear-gradient(to right, black calc(100% - 24px), transparent 100%);
    -webkit-mask-image: linear-gradient(to right, black calc(100% - 24px), transparent 100%);
  `
      : ""}
`;

// Keeps the actions (e.g. Clear Filter) on one line at their natural width;
// the label group shrinks instead.
const ActionsSlot = styled.div`
  flex-shrink: 0;
  white-space: nowrap;
  margin-left: 1.2rem;
`;

const TabButton = styled.button<{ $active: boolean; $isSingleTab: boolean }>`
  appearance: none;
  border: none;
  cursor: ${({ $isSingleTab }) => ($isSingleTab ? "default" : "pointer")};
  background: transparent;
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: ${({ $isSingleTab }) => ($isSingleTab ? "0" : "0.7rem 1.2rem")};
  border-radius: 999px;
  isolation: isolate;
  transition: padding-left 0.25s ease;
  min-width: 0;

  &:first-child {
    padding-left: ${({ $active, $isSingleTab }) =>
      $isSingleTab ? "0" : $active ? "1.2rem" : "0.4rem"};
  }
`;

const ActivePill = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: ${t("grey10")};
  border-radius: 999px;
  z-index: 0;
`;

const TabContent = styled.span`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;

  /* keep the leading icon at full size while the label truncates */
  & > svg {
    flex-shrink: 0;
  }
`;

const TabText = styled(UI_BODY_SEMIBOLD_SM_DF)<{ $active: boolean; $isSingleTab: boolean }>`
  color: ${({ $active, $isSingleTab }) =>
    !$isSingleTab && $active ? t("onSecondary") : t("grey80")};
  transition: color 0.2s ease;
  min-width: 0;
  white-space: nowrap;

  &:hover {
    color: ${({ $active, $isSingleTab }) =>
      !$isSingleTab && $active ? t("onSecondary") : t("grey100")};
  }
`;

export function AnimatedTabs<T extends string>({
  tabs,
  value,
  onChange,
  padding,
  actions,
}: AnimatedTabsProps<T>) {
  // Single-tab mode: there's nothing to switch between, so suppress the
  // active pill background and drop the button's horizontal padding so the
  // label sits flush like a static heading instead of looking like a chip.
  const isSingleTab = tabs.length === 1;
  return (
    <AnimateSharedLayout>
      <Bar $padding={padding} $spread={!!actions}>
        <TabButtonGroup $fade={!!actions}>
        {tabs.map((tab) => {
          const active = tab.value === value;
          // String → look up the oolib icon by name; otherwise it's already an
          // icon component (e.g. a Phosphor `*Icon`), use it directly.
          const IconComp = tab.icon
            ? typeof tab.icon === "string"
              ? icons[tab.icon]
              : tab.icon
            : null;
          return (
            <TabButton
              key={tab.value}
              $active={active}
              $isSingleTab={isSingleTab}
              onClick={() => onChange(tab.value)}
            >
              {active && !isSingleTab && (
                <ActivePill
                  layoutId="animated-tabs-active-pill"
                  transition={PILL_TRANSITION}
                />
              )}
              <TabContent>
                {IconComp && (
                  <IconComp
                    size={16}
                    weight="bold"
                    color={!isSingleTab && active ? t("onSecondary") : t("grey80")}
                  />
                )}
                <TabText $active={active} $isSingleTab={isSingleTab}>
                  {tab.label}
                </TabText>
              </TabContent>
            </TabButton>
          );
        })}
        </TabButtonGroup>
        {actions && <ActionsSlot>{actions}</ActionsSlot>}
      </Bar>
    </AnimateSharedLayout>
  );
}

function useMeasureHeight(deps: React.DependencyList = []) {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const [height, setHeight] = useState(0);

  // During AnimatePresence transitions the new slider mounts before the old
  // one unmounts. The old unmount fires `ref(null)` last, which would clear
  // our observed element. Ignore null to keep observing the most recent
  // mounted element.
  const setRef = useCallback((node: HTMLDivElement | null) => {
    if (node) setEl(node);
  }, []);

  useResizeObserver(el, (entry) => {
    const next = entry.contentRect.height;
    setHeight((prev) => (prev === next ? prev : next));
  });

  useLayoutEffect(() => {
    if (!el) return;
    const next = el.getBoundingClientRect().height;
    setHeight((prev) => (prev === next ? prev : next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [el, ...deps]);

  return [setRef, height] as const;
}

type TabsPanelProps<T extends string> = {
  tabs: readonly T[];
  value: T;
  children: React.ReactNode;
  padding?: Padding;
  /** When set, the panel content scrolls inside a container capped at this
   *  height (instead of the caller wrapping children in their own scroller). */
  scrollMaxHeight?: number | string;
  /** Ref to the scroll container — for programmatic scrolling by the caller. */
  scrollRef?: React.Ref<HTMLDivElement>;
};

const PanelPadded = styled.div<{ $padding?: Padding }>`
  ${({ $padding }) =>
    $padding !== undefined
      ? `padding: ${typeof $padding === "number" ? `${$padding}px` : $padding};`
      : ""}
`;

// Scroll container for the panel content. overflow-y: scroll keeps the track
// always reserved; the thin custom scrollbar matches the V3 light theme.
const PanelScroll = styled.div`
  overflow-y: scroll;

  scrollbar-width: thin;
  scrollbar-color: ${t("grey20")} transparent;
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${t("grey20")};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${t("grey30")};
  }
`;

const PanelOuter = styled(motion.div)`
  position: relative;
  overflow: hidden;
`;

const PanelSlider = styled(motion.div)<{ $hasHeight: boolean }>`
  ${({ $hasHeight }) =>
    $hasHeight
      ? `position: absolute; top: 0; left: 0; width: 100%;`
      : `position: relative;`}
`;

// direction === 0 (initial render or same-tab) → no horizontal offset, just fade.
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? "100%" : direction < 0 ? "-100%" : 0,
    opacity: 0,
  }),
  center: { x: 0, opacity: 1 },
  exit: (direction: number) => ({
    x: direction > 0 ? "-100%" : direction < 0 ? "100%" : 0,
    opacity: 0,
  }),
};

export function TabsPanel<T extends string>({
  tabs,
  value,
  children,
  padding,
  scrollMaxHeight,
  scrollRef,
}: TabsPanelProps<T>) {
  const [setRef, height] = useMeasureHeight([value, children, scrollMaxHeight]);

  const currentIndex = tabs.indexOf(value);
  const prevIndexRef = useRef(currentIndex);
  const direction = currentIndex - prevIndexRef.current;
  prevIndexRef.current = currentIndex;

  return (
    <MotionConfig transition={PANEL_TRANSITION}>
      <PanelOuter animate={{ height: height || "auto" }}>
        <AnimatePresence initial={false} custom={direction}>
          <PanelSlider
            key={value}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            $hasHeight={!!height}
          >
            <div ref={setRef}>
              <PanelScroll ref={scrollRef} style={{ maxHeight: scrollMaxHeight }}>
                <PanelPadded $padding={padding}>{children}</PanelPadded>
              </PanelScroll>
            </div>
          </PanelSlider>
        </AnimatePresence>
      </PanelOuter>
    </MotionConfig>
  );
}

type TabsProps<T extends string> = {
  tabs: AnimatedTab<T>[];
  value: T;
  onChange: (value: T) => void;
  children: React.ReactNode;
  /**
   * Outer padding for the tabs+panel block. A single value is smart-split so
   * the bar gets top+sides and the panel gets bottom+sides — keeping them
   * visually flush without padding-induced gap. A multi-value CSS shorthand
   * (e.g. "16px 24px 0 16px") is applied as-is to both bar and panel.
   * Use barPadding / panelPadding to override either side directly.
   */
  padding?: Padding;
  barPadding?: Padding;
  panelPadding?: Padding;
  /** Content pinned to the right edge of the tab bar (e.g. a Clear Filter
   *  button). Forwarded to AnimatedTabs. */
  actions?: React.ReactNode;
  /** Cap the panel content height and scroll inside it. Forwarded to TabsPanel. */
  scrollMaxHeight?: number | string;
  /** Ref to the panel's scroll container. Forwarded to TabsPanel. */
  scrollRef?: React.Ref<HTMLDivElement>;
};

const TabsContainer = styled.div<{ $hasContent: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $hasContent }) => ($hasContent ? "1.2rem" : "0")};
  transition: gap 0.27s cubic-bezier(0.32, 0.72, 0, 1);
`;

const normalizePadding = (p: Padding): string =>
  typeof p === "number" ? `${p}px` : p;

const isSingleValue = (p: Padding): boolean =>
  typeof p === "number" || !p.trim().includes(" ");

const splitPadding = (
  p: Padding,
  panelHasContent: boolean
): { bar: string; panel: string } => {
  const v = normalizePadding(p);
  if (!isSingleValue(p)) return { bar: v, panel: v };
  // No content → bar absorbs full perimeter; panel padding zeroed so the
  // always-mounted empty panel collapses cleanly to 0.
  if (!panelHasContent) return { bar: v, panel: "0" };
  return {
    bar: `${v} ${v} 0 ${v}`,
    panel: `0 ${v} ${v}`,
  };
};

export function Tabs<T extends string>({ tabs, value, onChange, children, padding, barPadding, panelPadding, actions, scrollMaxHeight, scrollRef }: TabsProps<T>) {
  const order = tabs.map((tab) => tab.value);
  const hasContent = React.Children.toArray(children).length > 0;
  const split = padding !== undefined ? splitPadding(padding, hasContent) : undefined;
  const resolvedBarPadding = barPadding ?? split?.bar;
  const resolvedPanelPadding = panelPadding ?? split?.panel;
  return (
    <TabsContainer $hasContent={hasContent}>
      <AnimatedTabs tabs={tabs} value={value} onChange={onChange} padding={resolvedBarPadding} actions={actions} />
      <TabsPanel tabs={order} value={value} padding={resolvedPanelPadding} scrollMaxHeight={scrollMaxHeight} scrollRef={scrollRef}>
        {children}
      </TabsPanel>
    </TabsContainer>
  );
}

export default AnimatedTabs;
